import _debounce from 'lodash/debounce';
import {
    any,
    array,
    arrayOf,
    bool,
    func,
    number,
    object,
    oneOfType,
    shape,
    string,
} from 'prop-types';
import React, { useState } from 'react';
import ReactSelect, { Async } from 'react-select';
import styled from 'styled-components';

import DropdownIndicator from 'ravenjs/lib/Select/DropdownIndicator';
import { callFunc } from 'ravenjs/utils/actions';
import { isEmpty } from 'ravenjs/utils/lodash';
import Badge from 'ravenjs/lib/Badge';
import { renderCheck } from 'ravenjs/utils/viewport';

import { DEFAULT_COLORS } from 'constants/colors';
import { DROPDOWN_FILTER_NAME } from 'constants/content';
import IdentifierPatch from 'components/IdentifierPatch';

import getSelectStyles from 'ravenjs/lib/Select/SelectStyles';

import ValueContainer from './ValueContainer';
import MultiSelectOption from './MultiSelectOption';

const ReactSelectContainer = styled.div`
    .document-dropdown-filter__control {
        .react-select-badge {
            &[style] {
                background-color: ${DEFAULT_COLORS.BLUE};
            }
        }
    }
`;

const DropDownListItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: ${({ justify }) => (justify ? 'flex-start' : 'space-between')};
    width: 100%;
`;

const SpanListItem = styled.span`
    color: ${DEFAULT_COLORS.BLACK};
    max-width: 70%;
`;

const DropdownFilter = props => {
    const {
        asyncCallback,
        closeMenuOnSelect,
        id,
        menuPlacement,
        name,
        onChange,
        isMulti,
        isSearchable,
        options,
        placeholder,
        value,
        showColor,
    } = props;
    const selectRef = React.createRef();
    const [isOpen, setIsOpen] = useState(false);
    const isMobile = renderCheck('md', 'less');
    const allOption = options.find(({ isAllOption }) => isAllOption);

    const handleOnChange = changedObj => {
        let updatedValue;
        if (isMulti) {
            if (allOption && isAllOptionSelected(changedObj)) {
                updatedValue = [
                    {
                        label: allOption.label,
                        value: allOption.value,
                    },
                ];
            } else {
                updatedValue = changedObj;
            }
        } else {
            updatedValue = changedObj.value;
        }
        if (!isMobile) {
            setIsOpen(false);
        }
        callFunc(onChange, name, updatedValue);
    };

    const handleOnMouseEnter = () => {
        setIsOpen(true);
    };

    const handleOnMouseLeave = () => {
        setIsOpen(false);
    };

    const isAllOptionSelected = changedSelectedOptions => {
        return Boolean(changedSelectedOptions.find(({ value }) => value === allOption.value));
    };

    const customStyles = {
        dropdownIndicator: {
            padding: '8px !important',
        },
        option: {
            alignItems: 'center',
            display: 'flex',
        },
        singleValue: {
            minWidth: renderCheck('md', 'less') ? 'calc(100% - 0.312rem)' : undefined,
        },
    };

    const styles = getSelectStyles(props, customStyles);

    const formatOptionLabel = option => (
        <DropDownListItem justify={showColor}>
            {option.color && (
                <IdentifierPatch color={option.color} margin="0 8px 0 0" size="0.875rem" />
            )}
            <SpanListItem>{option.label}</SpanListItem>
            {option.order && (
                <Badge
                    backgroundColor={DEFAULT_COLORS.LIGHT}
                    className="react-select-badge"
                    style={{
                        flex: '0',
                        width: 'inherit',
                        textTransform: 'uppercase',
                        fontWeight: '500',
                        fontSize: '0.625rem',
                        lineHeight: 'inherit',
                        whiteSpace: 'nowrap',
                        color: DEFAULT_COLORS.BLACK,
                    }}
                >
                    {option.order}
                </Badge>
            )}
        </DropDownListItem>
    );

    const mouseEventProps = isMobile
        ? {}
        : {
              onMouseEnter: handleOnMouseEnter,
              onMouseLeave: handleOnMouseLeave,
          };

    const loadOptions = _debounce((inputValue, callback) => {
        asyncCallback(inputValue)
            .then(updatedOptions => {
                return callback(updatedOptions);
            })
            .catch(() => {
                return callback([]);
            });
    }, 500);

    const getSelectedValue = () => {
        if (isMulti) {
            return isEmpty(value) ? [] : value;
        }
        return options.find(option => option.value === value) || null;
    };

    const getComponents = () => {
        const components = { DropdownIndicator };
        if (isMulti) {
            components.MultiValue = () => null;
            components.Option = MultiSelectOption;
            components.ValueContainer = ValueContainer;
        }
        return components;
    };
    const selectProps = {
        blurInputOnSelect: closeMenuOnSelect,
        closeMenuOnSelect,
        id,
        isMulti,
        isSearchable,
        classNamePrefix: 'document-dropdown-filter',
        components: getComponents(),
        hideSelectedOptions: false,
        ref: selectRef,
        menuIsOpen: isMobile ? undefined : isOpen,
        menuPlacement,
        onChange: handleOnChange,
        options,
        placeholder,
        value: getSelectedValue(),
        styles,
        formatOptionLabel: DROPDOWN_FILTER_NAME.SORT === name || showColor ? formatOptionLabel : '',
    };

    if (asyncCallback && typeof asyncCallback === 'function') {
        return (
            <ReactSelectContainer {...mouseEventProps}>
                <Async {...selectProps} defaultOptions loadOptions={loadOptions} />
            </ReactSelectContainer>
        );
    }

    return (
        <ReactSelectContainer {...mouseEventProps}>
            <ReactSelect {...selectProps} />
        </ReactSelectContainer>
    );
};

DropdownFilter.propTypes = {
    asyncCallback: func,
    closeMenuOnSelect: bool,
    id: oneOfType([number, string]).isRequired,
    menuPlacement: string,
    name: string.isRequired,
    onChange: func,
    options: arrayOf(
        shape({
            isAllOption: bool,
            label: string,
            value: any.isRequired,
        })
    ),
    placeholder: string,
    value: oneOfType([number, string, array, object]),
    showColor: bool,
    isMulti: bool,
    isSearchable: bool,
};

DropdownFilter.defaultProps = {
    asyncCallback: null,
    closeMenuOnSelect: true,
    menuPlacement: 'bottom',
    onChange: null,
    options: [],
    placeholder: null,
    value: null,
    showColor: false,
    isMulti: false,
    isSearchable: false,
};

export default DropdownFilter;
